// @ts-nocheck
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import {
  CONSTRAINT_TYPE_LABELS,
  SEVERITY_LABELS,
  STATUS_LABELS,
  SOURCE_LABELS,
  humanizeConstraint,
} from "@/lib/constraints/humanize";
import type { ConstraintRow } from "./ConstraintCard";

interface Props {
  constraint: ConstraintRow | null;
  teamName: string | null;
  opponentNameLookup: (teamId: string) => string | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function ReviewConstraintDialog({
  constraint,
  teamName,
  opponentNameLookup,
  open,
  onOpenChange,
  onSuccess,
}: Props) {
  const [saving, setSaving] = useState(false);
  const [severity, setSeverity] = useState("hard");
  const [responseNotes, setResponseNotes] = useState("");
  const [partialText, setPartialText] = useState("");
  const [pendingAction, setPendingAction] = useState<string | null>(null);

  // "Apply to the rest of this pool" — preview first, then create the copies.
  const [poolPreview, setPoolPreview] = useState<any[] | null>(null);
  const [poolBusy, setPoolBusy] = useState(false);

  const derivedFrom = constraint?.parameters?.derived_from_constraint_id ?? null;

  const loadPoolPreview = async () => {
    setPoolBusy(true);
    const { data, error } = await supabase.rpc("preview_constraint_pool_apply", {
      p_constraint_id: constraint.id,
    });
    setPoolBusy(false);
    if (error) {
      toast.error(error.message || "Could not work out this team's pool");
      return;
    }
    setPoolPreview(data || []);
  };

  const applyToPool = async () => {
    setPoolBusy(true);
    const { data, error } = await supabase.rpc("apply_constraint_to_pool", {
      p_constraint_id: constraint.id,
    });
    setPoolBusy(false);
    if (error) {
      toast.error(error.message || "Could not apply this request to the pool");
      return;
    }
    const created = data?.created ?? 0;
    const skipped = (data?.skipped ?? []).length;
    toast.success(
      `${created} team${created === 1 ? "" : "s"} given a copy` +
        (skipped ? ` · ${skipped} skipped (already had a request of this type)` : "")
    );
    setPoolPreview(null);
    onOpenChange(false);
    onSuccess();
  };

  const removeDerived = async () => {
    setPoolBusy(true);
    const { data, error } = await supabase.rpc("remove_derived_constraints", {
      p_constraint_id: constraint.id,
    });
    setPoolBusy(false);
    if (error) {
      toast.error(error.message || "Could not remove the copies");
      return;
    }
    toast.success(`${data ?? 0} copy${(data ?? 0) === 1 ? "" : "ies"} removed. The original is unchanged.`);
    onOpenChange(false);
    onSuccess();
  };

  useEffect(() => {
    if (constraint) {
      setSeverity(constraint.severity);
      setResponseNotes(constraint.response_notes || "");
      setPartialText(
        constraint.partial_accommodation
          ? typeof constraint.partial_accommodation === "string"
            ? constraint.partial_accommodation
            : JSON.stringify(constraint.partial_accommodation, null, 2)
          : ""
      );
      setPendingAction(null);
      setPoolPreview(null);
    }
  }, [constraint]);

  if (!constraint) return null;

  const isPending = constraint.status === "pending_review";
  const isReviewed = !isPending;

  const handleReview = async (newStatus: string) => {
    if (newStatus === "denied" && !responseNotes.trim()) {
      toast.error("Response notes are required when denying a request.");
      return;
    }

    if (newStatus === "partial") {
      try {
        JSON.parse(partialText || "{}");
      } catch {
        toast.error("Partial accommodation must be valid JSON.");
        return;
      }
    }

    setSaving(true);

    const args: Record<string, any> = {
      p_constraint_id: constraint.id,
      p_new_status: newStatus,
    };

    if (responseNotes.trim()) {
      args.p_response_notes = responseNotes.trim();
    }

    if (newStatus === "partial" && partialText.trim()) {
      args.p_partial_accommodation = JSON.parse(partialText);
    }

    if (severity !== constraint.severity) {
      args.p_severity_override = severity;
    }

    const { error } = await supabase.rpc("review_schedule_constraint", args);

    if (error) {
      console.error("Error reviewing constraint:", error);
      toast.error("Failed to update constraint");
      setSaving(false);
      return;
    }

    toast.success(`Constraint ${STATUS_LABELS[newStatus]?.toLowerCase() || newStatus}`);
    setSaving(false);
    onOpenChange(false);
    onSuccess();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Review Constraint</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Read-only summary */}
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Team</span>
              <span className="font-medium">{teamName || "Event-wide"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Type</span>
              <span className="font-medium">
                {CONSTRAINT_TYPE_LABELS[constraint.constraint_type] || constraint.constraint_type}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Details</span>
              <span className="font-medium text-right max-w-[60%]">
                {humanizeConstraint(constraint, { opponentNameLookup })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Source</span>
              <Badge variant="secondary" className="text-xs">
                {SOURCE_LABELS[constraint.source] || constraint.source}
              </Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Status</span>
              <span className="font-medium">
                {STATUS_LABELS[constraint.status] || constraint.status}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Submitted</span>
              <span>{new Date(constraint.created_at).toLocaleString()}</span>
            </div>
          </div>

          {/* Existing response notes (read-only for reviewed) */}
          {isReviewed && constraint.response_notes && (
            <div className="space-y-1">
              <label className="text-sm font-medium">Response notes</label>
              <p className="text-sm bg-muted p-2 rounded">{constraint.response_notes}</p>
            </div>
          )}
          {isReviewed && constraint.partial_accommodation && (
            <div className="space-y-1">
              <label className="text-sm font-medium">Partial accommodation</label>
              <pre className="text-xs bg-muted p-2 rounded overflow-auto max-h-32">
                {typeof constraint.partial_accommodation === "string"
                  ? constraint.partial_accommodation
                  : JSON.stringify(constraint.partial_accommodation, null, 2)}
              </pre>
            </div>
          )}

          {/* Severity selector */}
          <div className="space-y-1">
            <label className="text-sm font-medium">Severity</label>
            <Select value={severity} onValueChange={setSeverity}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(SEVERITY_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Action-specific inputs */}
          {(pendingAction === "denied" || pendingAction === "partial") && (
            <div className="space-y-1">
              <label className="text-sm font-medium">
                {pendingAction === "denied" ? "Response notes (required)" : "Partial accommodation (JSON)"}
              </label>
              {pendingAction === "denied" ? (
                <Textarea
                  value={responseNotes}
                  onChange={(e) => setResponseNotes(e.target.value)}
                  placeholder="Explain why this request is denied..."
                  rows={3}
                />
              ) : (
                <Textarea
                  value={partialText}
                  onChange={(e) => setPartialText(e.target.value)}
                  placeholder='{"notes": "Moved to 11am instead of 10am"}'
                  rows={3}
                  className="font-mono text-xs"
                />
              )}
            </div>
          )}
          {/* Apply to the rest of this pool. Copies are the organizer's own:
              no fee, and they can be removed again as a group. */}
          {constraint.status === "approved" && constraint.team_id && !derivedFrom && (
            <div className="space-y-2 rounded border p-3">
              <p className="text-sm font-medium">Apply to the rest of this pool</p>
              <p className="text-xs text-muted-foreground">
                Gives every other team in this team's pool the same request. The copies are
                yours, not the coaches' — nobody is charged, and no refund can come from one.
                This request is left exactly as it is.
              </p>

              {poolPreview === null ? (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={loadPoolPreview} disabled={poolBusy}>
                    {poolBusy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Preview
                  </Button>
                  <Button size="sm" variant="ghost" onClick={removeDerived} disabled={poolBusy}>
                    Remove copies
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {poolPreview.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No other team is in this pool.</p>
                  ) : (
                    <ul className="text-xs space-y-0.5 max-h-40 overflow-auto">
                      {poolPreview.map((t: any) => (
                        <li key={t.registration_id} className={t.will_copy ? "" : "text-muted-foreground"}>
                          {t.will_copy ? "• " : "– "}
                          {t.team_name}
                          {!t.will_copy && ` — skipped: ${t.skip_reason}`}
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={applyToPool}
                      disabled={poolBusy || !poolPreview.some((t: any) => t.will_copy)}
                    >
                      {poolBusy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      Apply to {poolPreview.filter((t: any) => t.will_copy).length} team
                      {poolPreview.filter((t: any) => t.will_copy).length === 1 ? "" : "s"}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setPoolPreview(null)} disabled={poolBusy}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {derivedFrom && (
            <p className="rounded border bg-muted p-2 text-xs text-muted-foreground">
              This is a copy created from another team's approved request. Nobody paid for it.
              Remove it from the original request's "Remove copies" action.
            </p>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {isPending && !pendingAction && (
            <>
              <Button
                onClick={() => handleReview("approved")}
                disabled={saving}
                className="bg-green-600 hover:bg-green-700"
              >
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Approve
              </Button>
              <Button
                variant="outline"
                onClick={() => setPendingAction("partial")}
                disabled={saving}
              >
                Mark Partial
              </Button>
              <Button
                variant="outline"
                className="text-red-600 hover:text-red-700"
                onClick={() => setPendingAction("denied")}
                disabled={saving}
              >
                Deny
              </Button>
            </>
          )}

          {isPending && pendingAction && (
            <>
              <Button
                onClick={() => handleReview(pendingAction)}
                disabled={saving}
              >
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Confirm {pendingAction === "denied" ? "Deny" : "Partial"}
              </Button>
              <Button
                variant="ghost"
                onClick={() => setPendingAction(null)}
                disabled={saving}
              >
                Back
              </Button>
            </>
          )}

          {(constraint.status === "approved" || constraint.status === "partial") && (
            <Button
              variant="outline"
              className="text-red-600 hover:text-red-700"
              onClick={() => {
                setPendingAction("denied");
                if (!responseNotes) setResponseNotes("");
              }}
              disabled={saving}
            >
              Change to Denied
            </Button>
          )}

          {constraint.status === "denied" && (
            <Button
              variant="outline"
              onClick={() => handleReview("pending_review")}
              disabled={saving}
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Reopen as Pending
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}